<script setup>
import {computed, ref, watch} from 'vue'
import FormPageContentField from '@/common/form/FormPageContentField'
import ContentUsageCount from '@/common/ContentUsageCount'
import ItemStatusIcons from '@/common/ItemStatusIcons'
import Button from '@/common/Button.vue'
import api from '@/services/api'
import {usePageContentDataStore} from '@/stores/pageContentData'
import {useModalsStore} from '@/stores/modals'
import {useItemDataStore} from '@/stores/itemData'
import {useRoute, useRouter} from "vue-router"

const props = defineProps({
    pageContentLinkId: Number,
    contentAreas: Array,
    contentArea: String,
    contentAreaFirst: Boolean,
    contentAreaLast: Boolean,
    contentIndex: Number,
    contentLast: Boolean,
})

const router = useRouter()
const route = useRoute()

const pageContentDataStore = usePageContentDataStore()
const modalsStore = useModalsStore()
const itemDataStore = useItemDataStore()

const pageContentData = computed(() => {
    return pageContentDataStore.items.find((item) => item.id === props.pageContentLinkId)
})

const items = computed(() => {
    return pageContentDataStore.items.filter((obj) => {
        return (
            obj.pageId === pageContentData.value?.pageId &&
            obj.contentArea === props.contentArea
        )
    })
})

const isSiteTree = computed(() => route.name === 'siteTree')

const content = ref({})

watch(
    () => pageContentData.value?.contentId,
    async (contentId) => {
        if (!contentId) {
            content.value = {}
            return
        }
        content.value = (await itemDataStore.get({
            tableName: 'content',
            id: contentId
        })) || {}
    },
    {immediate: true}
)

const goToContent = () => {
    isSiteTree.value
        ? router.push({
            name: 'siteTree',
            params: {
                pageOrContentId: 'c' + props.pageContentLinkId
            }
        })
        : router.push({
            name: 'form',
            params: {
                component: 'content',
                id: pageContentData.value?.contentId
            }
        })
}

const moveUp = async () => {
    if (props.contentIndex === 0) {
        let i = props.contentAreas.indexOf(props.contentArea)
        let targetContentArea = props.contentAreas[i - 1]

        let areaItems = pageContentDataStore.items.filter((obj) => {
            return (
                obj.pageId === pageContentData.value.pageId &&
                obj.contentArea === targetContentArea
            )
        })
        let newDisplayOrder = areaItems.length + 1

        try {
            await api.post('api/move-page-content', {
                pageContentLinkId: pageContentData.value.id,
                contentArea: targetContentArea,
                displayOrder: newDisplayOrder
            })
            await pageContentDataStore.init()
        } catch (err) {
            // Handle error if needed
        }
    } else {
        try {
            await api.post('api/move-page-content', {
                pageContentLinkId: pageContentData.value.id,
                contentArea: props.contentArea,
                displayOrder: pageContentData.value.displayOrder - 1
            })
            await pageContentDataStore.init()
        } catch (err) {
            // Handle error if needed
        }
    }
}

const moveDown = async () => {
    if (props.contentLast) {
        let i = props.contentAreas.indexOf(props.contentArea)
        let targetContentArea = props.contentAreas[i + 1]

        try {
            await api.post('api/move-page-content', {
                pageContentLinkId: pageContentData.value.id,
                contentArea: targetContentArea,
                displayOrder: 1
            })
            await pageContentDataStore.init()
        } catch (err) {
            // Handle error if needed
        }
    } else {
        try {
            await api.post('api/move-page-content', {
                pageContentLinkId: pageContentData.value.id,
                contentArea: props.contentArea,
                displayOrder: pageContentData.value.displayOrder + 1
            })
            await pageContentDataStore.init()
        } catch (err) {
            // Handle error if needed
        }
    }
}

const removeContent = () => {
    modalsStore.show({
        componentName: 'ConfirmModal',
        obj: {
            modalTitle: 'Are you sure?',
            onConfirm: async () => {
                try {
                    await api.delete(`api/delete-page-content-link-id/${pageContentData.value.id}`)
                    await pageContentDataStore.init()
                } catch (err) {
                    // Handle error if needed
                }
            }
        }
    })
}
</script>

<template>
    <li v-if="pageContentData" :title="'#c' + pageContentLinkId"
        class="list-group-item py-1 pe-2 d-flex align-items-center">
        <div class="flex-fill">
            <FormPageContentField :content-id="pageContentData.contentId"/>
        </div>

        <ContentUsageCount :page-content-id="pageContentData.id" :show-menu="true" class="me-2 lh-1"/>

        <ItemStatusIcons v-if="content?.id" :id="content.id" :table-name="'content'"/>

        <Button class="btn-sm" @click="goToContent">
            <i class="bi-pencil-square"/>
        </Button>

        <Button :disabled="!contentIndex && contentAreaFirst" class="btn-sm" @click.prevent.stop="moveUp">
            <i class="bi-chevron-up"/>
        </Button>

        <Button :disabled="contentLast && contentAreaLast" class="btn-sm" @click.prevent.stop="moveDown">
            <i class="bi-chevron-down"/>
        </Button>

        <Button class="btn-sm" @click="removeContent">
            <i class="bi-x-lg"/>
        </Button>
    </li>
</template>

<style scoped>
.content-icon {
    padding: 0 10px;
}

.content-icon-unpublished {
    color: #d9534f;
}

.content-icon-pending {
    color: #198754;
}

.content-icon-expiring {
    color: #ffc107;
}

.content-icon-expired {
    color: #dc3545;
}
</style>